import React from 'react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useExceptionStatistics } from '@/hooks/useExceptions';
import { DashboardMetrics } from '../DashboardMetrics';
import { QuickActions } from '../QuickActions';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';
import Badge from '@/components/ui/Badge';

export function ExceptionsDashboard() {
  const { user } = useAuthContext();
  const { data: statsData, isLoading } = useExceptionStatistics();

  // Calculate metrics for exception holds
  const metrics = React.useMemo(() => {
    if (!statsData) {
      return {
        activeRequests: 0,
        pendingActions: 0,
        completedToday: 0,
        exceptionHolds: 0,
      };
    }
    
    return {
      activeRequests: statsData.totalInReview + statsData.totalApproved,
      pendingActions: statsData.totalInReview,
      completedToday: statsData.totalApproved,
      exceptionHolds: statsData.totalInReview + statsData.totalApproved + statsData.totalNotApproved + statsData.totalRevisionRequired,
    };
  }, [statsData]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">Exceptions Dashboard</h1>
        <p className="mt-2 text-neutral-600">
          Welcome back, {user?.firstName}! Review and approve exception hold requests.
        </p>
      </div>

      <Card className="bg-purple-50 border-purple-200">
        <CardHeader>
          <CardTitle className="text-purple-900">Enterprise-Wide Visibility</CardTitle>
          <CardDescription className="text-purple-700">
            You have access to all hold requests across all regions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-purple-800">
            As an Exceptions team member, you can view and approve exception holds from any region.
            Your approval is required for holds that exceed standard policy limits.
          </p>
        </CardContent>
      </Card>

      <DashboardMetrics metrics={metrics} />

      <QuickActions role="exceptions_member" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Exception Queue</CardTitle>
            <CardDescription>Items requiring approval</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900">In Review</p>
                    <p className="text-sm text-neutral-600">Awaiting your review</p>
                  </div>
                  <Badge className="bg-purple-100 text-purple-800">
                    {statsData?.totalInReview || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900">Approved</p>
                    <p className="text-sm text-neutral-600">Approved today</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800">
                    {statsData?.totalApproved || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900">Overdue</p>
                    <p className="text-sm text-neutral-600">Exceeding SLA</p>
                  </div>
                  <Badge className="bg-red-100 text-red-800">
                    {statsData?.expiringToday || 0}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Policy Compliance</CardTitle>
            <CardDescription>Exception categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <p className="font-medium text-neutral-900">Extended Duration</p>
                  <p className="text-sm text-neutral-600">Holds exceeding 10 days</p>
                </div>
                <Badge className="bg-neutral-100 text-neutral-900">-</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <p className="font-medium text-neutral-900">Movie Studio</p>
                  <p className="text-sm text-neutral-600">60-day gap requirements</p>
                </div>
                <Badge className="bg-neutral-100 text-neutral-900">-</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <p className="font-medium text-neutral-900">Policy Override</p>
                  <p className="text-sm text-neutral-600">Special circumstances</p>
                </div>
                <Badge className="bg-neutral-100 text-neutral-900">-</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
